import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'L21_accessToken',
  REFRESH_TOKEN: 'L21_refreshToken',
  PERSISTENCE: 'L21_persistence',
  IMPERSONATION_HANDOFF: 'L21_impersonation_handoff',
};

interface AuthTokens {
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn?: number;
}

export const authStorage = {
  getStorage: (persistent = false) => {
    if (Platform.OS !== 'web') {
      return AsyncStorage;
    }
    // For web, use localStorage for persistent storage and sessionStorage for session-only
    return persistent ? localStorage : sessionStorage;
  },

  async getTokens(persistent: boolean): Promise<AuthTokens> {
    const storage = this.getStorage(persistent);

    try {
      if (Platform.OS === 'web') {
        // For web, first check the specified storage
        const [primaryAccess, primaryRefresh] = await Promise.all([
          storage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
          storage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
        ]);

        if (primaryAccess && primaryRefresh) {
          return { accessToken: primaryAccess, refreshToken: primaryRefresh };
        }

        // If not found in specified storage and we're using sessionStorage,
        // check localStorage as fallback
        if (!persistent) {
          const [localAccess, localRefresh] = await Promise.all([
            localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
            localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
          ]);

          if (localAccess && localRefresh) {
            // Move tokens to session storage for this session
            await this.setTokens({ accessToken: localAccess, refreshToken: localRefresh }, false);
            // Clear localStorage if we're moving to session-only
            await Promise.all([
              localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
              localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
            ]);
            return { accessToken: localAccess, refreshToken: localRefresh };
          }
        }

        return { accessToken: primaryAccess, refreshToken: primaryRefresh };
      }

      // For mobile, just use AsyncStorage
      const [accessToken, refreshToken] = await Promise.all([
        storage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
        storage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
      ]);

      return { accessToken, refreshToken };
    } catch (error) {
      return { accessToken: null, refreshToken: null };
    }
  },

  async setTokens(tokens: AuthTokens, persistent: boolean): Promise<void> {
    const storage = this.getStorage(persistent);

    if (Platform.OS === 'web') {
      if (persistent) {
        // If persisting, clear session storage
        await Promise.all([
          sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
          sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
        ]);
      } else {
        // If using session storage, clear localStorage
        await Promise.all([
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
        ]);
      }
    }

    // Store tokens in the appropriate storage
    await Promise.all([
      tokens.accessToken && storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken),
      tokens.refreshToken && storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken),
    ]);
  },

  async clearTokens(): Promise<void> {
    if (Platform.OS === 'web') {
      // On web, always clear both storages to ensure a clean state
      await Promise.all([
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
        localStorage.removeItem(STORAGE_KEYS.PERSISTENCE),
        sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
        sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
      ]);
    } else {
      await Promise.all([
        AsyncStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
        AsyncStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
      ]);
    }
  },

  async getPersistence(): Promise<boolean> {
    // Always use localStorage for persistence setting
    const storage = Platform.OS === 'web' ? localStorage : AsyncStorage;
    try {
      const value = await storage.getItem(STORAGE_KEYS.PERSISTENCE);
      return value === 'true';
    } catch (error) {
      return false;
    }
  },

  async setPersistence(isPersistent: boolean): Promise<void> {
    // Always use localStorage for persistence setting
    const storage = Platform.OS === 'web' ? localStorage : AsyncStorage;
    await storage.setItem(STORAGE_KEYS.PERSISTENCE, String(isPersistent));
  },
};
